import { useQuery } from "@tanstack/react-query";

import { getExams } from "@/api/exams";
import { getWordSets } from "@/api/wordSets";
import { getWordSetSettings } from "@/api/wordSetSettings";
import { parseExamStatus } from "@/lib/examUtils";
import { useAuthStore } from "@/store/authStore";
import type { TestDateItem } from "@/types/common";
import type { Exam } from "@/types/models";

/**
 * 가입일 기준 날짜 목록 + 해당 날짜의 시험 정보를 조합하여 반환
 * - 최신 날짜가 먼저 오도록 역순 정렬
 * - 시험이 있으면 word_set_id → 단어장 제목으로 카테고리 표시
 * - 시험이 없으면 현재 설정의 활성 카테고리 표시
 */
export function useTestDatesQuery() {
  const user = useAuthStore(state => state.user);
  const userId = user?.id;
  const createdAt = user?.created_at;

  return useQuery({
    queryKey: ["testDates", userId],
    queryFn: async () => {
      const [exams, wordSets, settings] = await Promise.all([
        getExams(userId!),
        getWordSets(),
        getWordSetSettings(userId!),
      ]);

      return { exams, wordSets, settings };
    },
    enabled: Boolean(userId) && Boolean(createdAt),
    select: ({ exams, wordSets, settings }): TestDateItem[] => {
      const dates = generateDateRange(createdAt!);

      // word_set_id → title 맵
      const setTitleMap = new Map(wordSets.map(s => [s.id, s.title]));

      // 현재 활성 세트의 카테고리 (미생성 날짜에 표시)
      const activeSetId = settings.find(s => s.is_active)?.set_id;
      const activeCategory = activeSetId ? (setTitleMap.get(activeSetId) ?? null) : null;

      const dailyByDate = new Map<string, Exam>();
      const retriesByDate = new Map<string, Exam[]>();

      for (const exam of exams) {
        if (exam.exam_type === "daily") {
          dailyByDate.set(exam.exam_date, exam);
        } else if (exam.exam_type === "retry") {
          const list = retriesByDate.get(exam.exam_date) ?? [];
          list.push(exam);
          retriesByDate.set(exam.exam_date, list);
        }
      }

      return dates
        .map(date => {
          const daily = dailyByDate.get(date);
          const retries = retriesByDate.get(date) ?? [];

          // 시험이 있으면 word_set_id로 카테고리 조회, 없으면 활성 설정 카테고리
          const category = daily ? (setTitleMap.get(daily.word_set_id ?? "") ?? null) : activeCategory;

          return {
            date,
            category,
            exam: daily
              ? { id: daily.id, status: parseExamStatus(daily.status), score: Math.round(daily.score ?? 0) }
              : null,
            retryExams: retries
              .sort((a, b) => (a.retry_round ?? 0) - (b.retry_round ?? 0))
              .map(r => ({
                id: r.id,
                round: r.retry_round ?? 0,
                status: parseExamStatus(r.status),
                score: Math.round(r.score ?? 0),
              })),
          };
        })
        .reverse();
    },
  });
}

/** 로컬 타임존 기준 YYYY-MM-DD 문자열 반환 */
function toLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");

  return `${y}-${m}-${d}`;
}

/** 가입일부터 오늘까지 날짜 문자열 목록 생성 (로컬 타임존 기준) */
function generateDateRange(createdAt: string): string[] {
  const dates: string[] = [];
  const start = new Date(createdAt);
  start.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const current = new Date(start);

  while (current <= today) {
    dates.push(toLocalDateString(current));
    current.setDate(current.getDate() + 1);
  }

  return dates;
}
